import {
  Box,
  Button,
  Card,
  Heading,
  HStack,
  Image,
  Link,
  List,
  SimpleGrid,
  Stack,
  Text,
  VStack,
} from '@chakra-ui/react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { Check } from 'lucide-react'
import oilandgazImage from '~/assets/illustrations/oilandgazIllustrationMedium.jpeg'
import { cardHover, PageHeader, SectionHeading } from '~/components/shell'

export const Route = createFileRoute('/_app/oilandgas/')({ component: OilAndGasDemo })

function OilAndGasDemo() {
  const navigate = useNavigate()

  return (
    <Box maxW="1400px" mx="auto">
      <PageHeader title="Oil & Gas" subtitle="Pipeline monitoring and asset management" />

      <VStack align="stretch" gap={10}>
        {/* Customer story */}
        <Card.Root overflow="hidden" boxShadow="2xl">
          <Stack direction={{ base: 'column', md: 'row' }}>
            <Image
              objectFit="cover"
              maxW={{ base: '100%', md: '300px' }}
              src={oilandgazImage}
              alt="Oil and Gas"
            />
            <Stack flex={1}>
              <Card.Header>
                <Heading size="lg" color="gray.600">
                  Maximizing Production Uptime While Minimizing Risk
                </Heading>
              </Card.Header>
              <Card.Body>
                <VStack align="flex-start" gap={4} mb={'6'}>
                  <Text>
                    OG Company operates across the full oil and gas value chain - from drilling rigs
                    and production wells to pipelines, tankers, and distribution - but faced
                    critical challenges: unplanned equipment failures were causing costly production
                    losses, remote assets lacked real-time visibility, and safety incidents
                    threatened both workers and the environment.
                  </Text>
                  <Text>
                    By deploying the{' '}
                    <Link
                      href="https://www.hivemq.com/products/hivemq-platform/"
                      target="_blank"
                      rel="noreferrer"
                      color="blue.500"
                      _hover={{ textDecoration: 'underline' }}
                    >
                      HiveMQ Platform
                    </Link>
                    , OG Company built a unified monitoring infrastructure that connects sensors
                    across upstream, midstream, and downstream operations - enabling real-time
                    visibility and predictive insights from wellhead to delivery.
                  </Text>
                  <Text>
                    HiveMQ reliably collects data from assets in remote, harsh environments with
                    intermittent connectivity, ensuring critical operational and safety data is
                    never lost.
                  </Text>
                  <Text fontWeight="medium">Business Impact Delivered:</Text>
                  <List.Root pl={4}>
                    <List.Item>
                      <HStack gap={2}>
                        <Box p={0.5} bg="green.100" borderRadius="full">
                          <Check size={12} color="var(--chakra-colors-green-600)" />
                        </Box>
                        <Text fontSize="sm">
                          <b>30% reduction in unplanned downtime</b> through predictive maintenance
                          and early anomaly detection
                        </Text>
                      </HStack>
                    </List.Item>
                    <List.Item>
                      <HStack gap={2}>
                        <Box p={0.5} bg="green.100" borderRadius="full">
                          <Check size={12} color="var(--chakra-colors-green-600)" />
                        </Box>
                        <Text fontSize="sm">
                          <b>Improved safety outcomes</b> with real-time monitoring of pressure,
                          temperature, and leak detection across all assets
                        </Text>
                      </HStack>
                    </List.Item>
                    <List.Item>
                      <HStack gap={2}>
                        <Box p={0.5} bg="green.100" borderRadius="full">
                          <Check size={12} color="var(--chakra-colors-green-600)" />
                        </Box>
                        <Text fontSize="sm">
                          <b>20% reduction in maintenance costs</b> by shifting from scheduled to
                          condition-based maintenance
                        </Text>
                      </HStack>
                    </List.Item>
                    <List.Item>
                      <HStack gap={2}>
                        <Box p={0.5} bg="green.100" borderRadius="full">
                          <Check size={12} color="var(--chakra-colors-green-600)" />
                        </Box>
                        <Text fontSize="sm">
                          <b>Enhanced regulatory compliance</b> with complete audit trails and
                          environmental monitoring data
                        </Text>
                      </HStack>
                    </List.Item>
                  </List.Root>
                </VStack>
              </Card.Body>
            </Stack>
          </Stack>
        </Card.Root>

        {/* Platform journey */}
        <VStack align="stretch" gap={4}>
          <SectionHeading title="The Industrial Data Platform for Agentic AI" />
          <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} gap={6}>
            <Card.Root boxShadow="lg">
              <Card.Header>
                <Heading size="md" color="brand.solid">
                  Connect
                </Heading>
              </Card.Header>
              <Card.Body>
                <Text color="content.secondary">
                  Remote assets across upstream, midstream, and downstream connected via MQTT
                </Text>
              </Card.Body>
            </Card.Root>
            <Card.Root boxShadow="lg">
              <Card.Header>
                <Heading size="md" color="brand.solid">
                  Contextualize
                </Heading>
              </Card.Header>
              <Card.Body>
                <Text color="content.secondary">
                  Sensor data structured into governed safety and performance models
                </Text>
              </Card.Body>
            </Card.Root>
            <Card.Root boxShadow="lg">
              <Card.Header>
                <Heading size="md" color="brand.solid">
                  Analyze
                </Heading>
              </Card.Header>
              <Card.Body>
                <Text color="content.secondary">
                  Pressure, temperature, and leak anomalies monitored in real time
                </Text>
              </Card.Body>
            </Card.Root>
            <Card.Root boxShadow="lg">
              <Card.Header>
                <Heading size="md" color="brand.solid">
                  Act
                </Heading>
              </Card.Header>
              <Card.Body>
                <Text color="content.secondary">
                  Early warning signals trigger safety protocols and maintenance before incidents
                  occur
                </Text>
              </Card.Body>
            </Card.Root>
          </SimpleGrid>
        </VStack>

        {/* Data flow, UNS and AI */}
        <VStack align="stretch" gap={4}>
          <SectionHeading title="Data Flow, UNS and AI" />
          <SimpleGrid columns={{ base: 1, sm: 2, md: 3, lg: 4 }} gap={6}>
            <Card.Root
              overflow="hidden"
              transition="all 0.3s"
              _hover={cardHover}
              cursor="pointer"
              onClick={() => navigate({ to: '/oilandgas/flow' })}
              h="100%"
            >
              <Card.Header>
                <Heading size="md">Data Flow</Heading>
              </Card.Header>
              <Card.Body>
                <Text color="content.secondary">
                  Explore the oil &amp; gas data flow architecture.
                </Text>
              </Card.Body>
              <Card.Footer>
                <Button
                  w="100%"
                  onClick={(e) => {
                    e.stopPropagation()
                    navigate({ to: '/oilandgas/flow' })
                  }}
                >
                  View Flow
                </Button>
              </Card.Footer>
            </Card.Root>

            <Card.Root
              overflow="hidden"
              transition="all 0.3s"
              _hover={cardHover}
              cursor="pointer"
              onClick={() => navigate({ to: '/oilandgas/explorer' })}
              h="100%"
            >
              <Card.Header>
                <Heading size="md">UNS Explorer</Heading>
              </Card.Header>
              <Card.Body>
                <Text color="content.secondary">Explore the Unified Namespace in real-time.</Text>
              </Card.Body>
              <Card.Footer>
                <Button
                  w="100%"
                  onClick={(e) => {
                    e.stopPropagation()
                    navigate({ to: '/oilandgas/explorer' })
                  }}
                >
                  Explore UNS
                </Button>
              </Card.Footer>
            </Card.Root>

            <Card.Root
              overflow="hidden"
              transition="all 0.3s"
              _hover={cardHover}
              cursor="pointer"
              onClick={() => navigate({ to: '/oilandgas/bot' })}
              h="100%"
            >
              <Card.Header>
                <Heading size="md">AI Assistant</Heading>
              </Card.Header>
              <Card.Body>
                <Text color="content.secondary">Chat with AI about oil &amp; gas operations.</Text>
              </Card.Body>
              <Card.Footer>
                <Button
                  w="100%"
                  onClick={(e) => {
                    e.stopPropagation()
                    navigate({ to: '/oilandgas/bot' })
                  }}
                >
                  Chat with AI
                </Button>
              </Card.Footer>
            </Card.Root>

            <Card.Root
              overflow="hidden"
              transition="all 0.3s"
              _hover={cardHover}
              cursor="pointer"
              onClick={() => navigate({ to: '/oilandgas/knowledge-graph' })}
              h="100%"
            >
              <Card.Header>
                <Heading size="md">Knowledge Graph</Heading>
              </Card.Header>
              <Card.Body>
                <Text color="content.secondary">
                  Visualize the oil &amp; gas UNS as a real-time network graph.
                </Text>
              </Card.Body>
              <Card.Footer>
                <Button
                  w="100%"
                  onClick={(e) => {
                    e.stopPropagation()
                    navigate({ to: '/oilandgas/knowledge-graph' })
                  }}
                >
                  View Graph
                </Button>
              </Card.Footer>
            </Card.Root>
          </SimpleGrid>
        </VStack>
      </VStack>
    </Box>
  )
}
